import React, { useState } from "react";
import { useVideo } from "../../context/VideoContext";
import useVideoManager from "../../hooks/useVideoManager";
import LoadingModal from "./LoadingModal";
import "./DragDropZone.css";

const DragDropZone = ({ onIOSWarning }) => {
  const [isDragging, setIsDragging] = useState(false);
  const { isLoadingSubtitles, loadingMessage } = useVideo();
  const { handleFileProcessing } = useVideoManager();
  const fileInputRef = React.useRef(null);

  // 🎯 تشخیص iOS (فقط آیفون و آیپد)
  const isIOS = () => {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    );
  };

  // 🎯 فرمت‌هایی که در iOS پشتیبانی نمی‌شوند
  const IOS_UNSUPPORTED_EXTENSIONS = [
    ".mkv",
    ".webm",
    ".flv",
    ".wmv",
    ".avi",
    ".divx",
    ".xvid",
    ".rm",
    ".rmvb",
    ".asf",
    ".vob",
    ".mpg",
    ".mpeg",
    ".m2v",
    ".m1v",
    ".f4v",
    ".ts",
    ".mts",
    ".m2ts",
    ".ogv",
  ];

  const IOS_UNSUPPORTED_MIME_TYPES = [
    "video/x-matroska",
    "video/webm",
    "video/x-flv",
    "video/x-ms-wmv",
    "video/x-msvideo",
    "video/divx",
    "video/x-divx",
    "video/mp2t",
    "video/ogg",
  ];

  const isIOSUnsupportedFile = (file) => {
    if (!isIOS()) return false;
    const fileName = file.name.toLowerCase();
    return (
      IOS_UNSUPPORTED_EXTENSIONS.some((ext) => fileName.endsWith(ext)) ||
      (file.type &&
        IOS_UNSUPPORTED_MIME_TYPES.includes(file.type.toLowerCase()))
    );
  };

  const showIOSWarning = (file) => {
    const fileExtension = file.name.split(".").pop().toUpperCase();
    onIOSWarning({ isOpen: true, fileExtension });
  };

  const VALID_VIDEO_EXTENSIONS = [
    ".mp4",
    ".mkv",
    ".webm",
    ".mov",
    ".avi",
    ".flv",
    ".wmv",
    ".m4v",
    ".ts",
    ".3gp",
    ".ogg",
    ".ogv",
    ".mts",
    ".m2ts",
    ".divx",
    ".xvid",
    ".rm",
    ".rmvb",
    ".asf",
    ".vob",
    ".mpg",
    ".mpeg",
    ".m2v",
    ".m1v",
    ".f4v",
  ];

  const VALID_VIDEO_MIME_TYPES = [
    "video/mp4",
    "video/x-matroska",
    "video/webm",
    "video/quicktime",
    "video/x-msvideo",
    "video/x-flv",
    "video/x-ms-wmv",
    "video/mpeg",
    "video/ogg",
    "video/3gpp",
    "video/3gpp2",
    "video/mp2t",
    "video/x-m4v",
    "video/divx",
    "video/x-divx",
  ];

  const isValidVideoFile = (file) => {
    if (!file) return false;
    if (file.type && VALID_VIDEO_MIME_TYPES.includes(file.type.toLowerCase()))
      return true;
    return VALID_VIDEO_EXTENSIONS.some((ext) =>
      file.name.toLowerCase().endsWith(ext)
    );
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files?.length > 0) {
      const file = files[0];

      if (isIOSUnsupportedFile(file)) {
        showIOSWarning(file);
        return;
      }

      if (!isValidVideoFile(file)) {
        alert("⚠️ لطفاً یک فایل ویدیویی معتبر انتخاب کنید.");
        return;
      }

      await handleFileProcessing(file);
    }
  };

  const handleFileInputChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (isIOSUnsupportedFile(file)) {
      showIOSWarning(file);
      e.target.value = "";
      return;
    }

    if (!isValidVideoFile(file)) {
      alert("⚠️ لطفاً یک فایل ویدیویی معتبر انتخاب کنید.");
      e.target.value = "";
      return;
    }

    try {
      await handleFileProcessing(file);
    } finally {
      e.target.value = "";
    }
  };

  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleDivClick = (e) => {
    if (
      e.target === e.currentTarget ||
      e.target.classList.contains("drag-drop-content") ||
      e.target.closest(".drag-drop-content") ===
        e.currentTarget.querySelector(".drag-drop-content")
    ) {
      e.preventDefault();
      e.stopPropagation();
      fileInputRef.current?.click();
    }
  };

  return (
    <>
      <div
        className={`drag-drop-zone ${isDragging ? "active" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleDivClick}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileInputChange}
          style={{ display: "none" }}
        />

        <div className="drag-drop-content">
          <div className="drag-drop-icon">
            <i className="fas fa-cloud-upload-alt"></i>
          </div>
          <div className="drag-drop-text">
            <h3>ویدیو را اینجا رها کنید</h3>
            <p>یا برای انتخاب فایل کلیک کنید</p>
            <p className="auto-play-note">⚠️ ویدیو به صورت خودکار پخش می‌شود</p>
          </div>
          <button
            type="button"
            className="drag-drop-button"
            onClick={handleButtonClick}
          >
            انتخاب ویدیو
          </button>
        </div>
      </div>

      {isLoadingSubtitles && <LoadingModal message={loadingMessage} />}
    </>
  );
};

export default DragDropZone;
